import moment from 'moment';
import React from 'react';
import Item from './Item';
import { CheckCircleOutlined, CalendarOutlined, FileTextOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import Date from '../../../components/Formatter/Date';
import { Image, Card, Row, Col, Typography, Space } from 'antd';

const { Text } = Typography;

const calculateDuration = (startDate, endDate) => {
  if (!startDate) return null;
  const start = moment(startDate);
  const end = endDate ? moment(endDate) : moment();
  if (!start.isValid() || !end.isValid()) return null;
  const months = Math.floor(end.diff(start, 'months', true));
  if (months < 0) return null;
  const years = Math.floor(months / 12);
  const remMonths = Math.floor(months % 12);
  return `${years ? `${years} Tahun ` : ''}${remMonths} Bulan`;
};

const Skills = ({ passport }) => {
  const ppStartDate =
    passport.work_experience_pp?.start_date ||
    passport.work_experience_pp?.since ||
    passport.work_experience_pp_start_date;
  const ppEndDate =
    passport.work_experience_pp?.end_date ||
    passport.work_experience_pp?.until ||
    passport.work_experience_pp_end_date;
  const ppDuration =
    passport.work_experience_pp?.duration || calculateDuration(ppStartDate, ppEndDate);

  const nonPpStartDate =
    passport.work_experience_nonpp?.start_date ||
    passport.work_experience_nonpp?.since ||
    passport.work_experience_nonpp_start_date;
  const nonPpEndDate =
    passport.work_experience_nonpp?.end_date ||
    passport.work_experience_nonpp?.until ||
    passport.work_experience_nonpp_end_date;
  const nonPpDuration =
    passport.work_experience_nonpp?.duration || calculateDuration(nonPpStartDate, nonPpEndDate);

  return (
    <>
      {passport.work_experience && passport.work_experience.since !== null ? (
        <>
          <Item label={`Mulai Bekerja`} value={<Date value={passport.work_experience.since}/>} />
          <Item label={`Pengalaman Bekerja`} value={passport.work_experience.duration} />
        </>
      ) : (
        <>
          {(ppStartDate || ppEndDate || ppDuration) && (
            <>
              {ppStartDate && <Item label={`Mulai Bekerja PP`} value={<Date value={ppStartDate} />} />}
              {ppEndDate && <Item label={`Selesai Bekerja PP`} value={<Date value={ppEndDate} />} />}
              {ppDuration && <Item label={`Pengalaman Bekerja PP`} value={ppDuration} />}
            </>
          )}
          {(nonPpStartDate || nonPpEndDate || nonPpDuration) && (
            <>
              {nonPpStartDate && <Item label={`Mulai Bekerja Non PP`} value={<Date value={nonPpStartDate} />} />}
              {nonPpEndDate && <Item label={`Selesai Bekerja Non PP`} value={<Date value={nonPpEndDate} />} />}
              {nonPpDuration && <Item label={`Pengalaman Bekerja Non PP`} value={nonPpDuration} />}
            </>
          )}
        </>
      )}
      <Item label={`Spesialisasi Pekerjaan`} value={passport.job_specializations.map((item, key) => (
        <span key={key}>{item.label}</span>
      ))} />
      <Item label={`Kompetensi`} value={<SkillsList skills={passport.skills} />} />
      {/* training competency */}
      <Item label={`Pelatihan yang pernah diikuti`} value={<TrainingCompetencyList competencies={passport.training_competencies} />} />
      {passport.skills_file && Array.isArray(passport.skills_file) && passport.skills_file.length > 0 ? (
        <Item label={`Sertifikasi Kompetensi`} value={
          <Row gutter={[16, 16]}>
            {passport.skills_file.map((cert) => (
              <Col xs={24} sm={24} md={12} xl={8} key={cert.id}>
                <Card 
                  hoverable 
                  bodyStyle={{ padding: '16px' }}
                  style={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid #e8e8e8', height: '100%' }}
                >
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <div style={{ 
                      height: '160px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      background: '#fafafa', 
                      borderRadius: '8px', 
                      overflow: 'hidden' 
                    }}>
                      {cert.is_image ? (
                        <Image 
                          src={cert.file_url} 
                          alt="Sertifikat" 
                          style={{ maxHeight: '160px', objectFit: 'contain' }}
                          preview={{ mask: 'Lihat Sertifikat' }}
                        />
                      ) : (
                        <div style={{ textAlign: 'center' }}>
                          <FileTextOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '12px' }} />
                          <div>
                            <a href={cert.file_url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 500 }}>
                              Buka Dokumen
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <Text style={{ fontSize: '14px', fontWeight: 500, color: '#262626' }}>
                        <SafetyCertificateOutlined style={{ marginRight: '6px', color: '#52c41a' }} />
                        Detail Sertifikasi
                      </Text>
                      <Space direction="vertical" size={2} style={{ marginTop: '4px' }}>
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                          <CalendarOutlined style={{ marginRight: '6px' }} />
                          Terbit: <Text strong>{cert.tgl_terbit ? <Date value={cert.tgl_terbit} /> : "-"}</Text>
                        </Text>
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                          <CalendarOutlined style={{ marginRight: '6px' }} />
                          Berlaku s/d: <Text strong>{cert.tgl_expired ? <Date value={cert.tgl_expired} /> : "-"}</Text>
                        </Text>
                      </Space>
                    </div>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        } />
      ) : (
        <Item label="Sertifikasi Kompetensi" value={<Text type="secondary">Belum ada sertifikasi yang diunggah</Text>} />
      )}
    </>
  );
}

export default Skills;

const SkillsList = ({ skills }) => {
  return skills.length > 0 ? (
    <>
      {skills.map((skill, key) => {
        return <div className="gx-mb-2" key={`skill-item-${key}`}>
          <CheckCircleOutlined /> {skill.label}
        </div>;
      })}
    </>
  ):"Tidak ada kompetensi dipilih";
}

const TrainingCompetencyList = ({ competencies }) => {
  return competencies && competencies.length > 0 ? (
    <>
      {competencies.map((item, key) => {
        return <div className="gx-mb-2" key={`tc-item-${key}`}>
          <CheckCircleOutlined /> {item.label}
        </div>;
      })}
    </>
  ):"-";
}